// src/components/TodaysDate.ts
import "./TodaysDate.css";
import { getClientTimeContext } from "../utils/clientTimeContext";
import { getFreshWeatherLocationContext } from "./UserLocation";

const TIME_API_URL = "https://api.ayesearch.com/api/ai/time/";

type TimeApiResponse = {
  deterministic?: boolean;
  response_type?: string;
  date?: string;
  period?: string;
  location?: string;
};

function normalizeQuery(value: string): string {
  return String(value || "")
    .trim()
    .replace(/[?!.,]+$/g, "")
    .replace(/\s+/g, " ")
    .replace(/\bw\s+hat\b/gi, "what")
    .replace(/\bw\s+hen\b/gi, "when")
    .replace(/\btommorow\b/gi, "tomorrow")
    .replace(/\btommorrow\b/gi, "tomorrow")
    .replace(/\bnet\s+week\b/gi, "next week")
    .replace(/\bnxt\s+week\b/gi, "next week")
    .replace(/\bnextweek\b/gi, "next week")
    .replace(/\blastweek\b/gi, "last week");
}

export function isTodaysDateQuery(query: string): boolean {
  const value = normalizeQuery(query).toLowerCase();
  if (!value) return false;
  if (/\b(weather|forecast|temperature)\b/.test(value)) return false;
  if (/\bwhat time\b|\bcurrent time\b|\blocal time\b/.test(value)) return false;

  const direct =
    (/\b(today|today's|todays|current date)\b/.test(value) &&
      /\b(date|day|today)\b/.test(value)) ||
    /^(?:date|what(?:'s| is)? (?:the )?date|what date is it|what day is it|my date)$/i.test(value);

  const relative =
    /\b(tomorrow|yesterday|next week|last week)\b/.test(value) &&
    (
      /\b(date|day|when)\b/.test(value) ||
      /\bwhat\s+(?:is|was|will)\b/.test(value) ||
      /^(?:tomorrow|yesterday|next week|last week)$/i.test(value)
    );

  const arithmetic =
    /\b(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|ninety)\s+(?:day|days|week|weeks|month|months|year|years)\s+(?:ago|before today|before now|from today|after today|from now|later)\b/.test(value);

  return direct || relative || arithmetic;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function localLongDate(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function bindQuestions(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>("[data-date-question]").forEach((button) => {
    button.addEventListener("click", () => {
      const expanded = button.getAttribute("aria-expanded") === "true";
      const answer = container.querySelector<HTMLElement>(
        `#${button.getAttribute("aria-controls") || ""}`,
      );
      const chevron = button.querySelector<HTMLElement>(".aye-date-chevron");
      button.setAttribute("aria-expanded", expanded ? "false" : "true");
      if (answer) answer.hidden = expanded;
      if (chevron) chevron.textContent = expanded ? "⌄" : "⌃";
    });
  });
}

async function fetchDeterministicDate(query: string): Promise<TimeApiResponse | null> {
  const { timezone, locale, timezoneOffsetMinutes } = getClientTimeContext();
  const url = new URL(TIME_API_URL);
  url.searchParams.set("q", query);
  if (timezone) url.searchParams.set("timezone", timezone);
  if (locale) url.searchParams.set("locale", locale);
  url.searchParams.set("timezone_offset_minutes", String(timezoneOffsetMinutes));

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "omit",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) return null;

  const data = (await response.json()) as TimeApiResponse;
  return data.deterministic === true &&
    String(data.response_type || "").toLowerCase() === "date" &&
    String(data.date || "").trim()
      ? data
      : null;
}

function relatedQuestions(data: TimeApiResponse): Array<{ question: string; answer: string }> {
  const date = String(data.date || "").trim();
  const location = String(data.location || "").trim();
  const place = location ? ` in ${location}` : "";

  if (data.period === "next_week") return [
    { question: `When does next week begin${place}?`, answer: `Next week${place} begins on ${date}.` },
    { question: `What day is it next week${place}?`, answer: `The next Monday-based calendar week${place} begins on ${date}.` },
    { question: `What was the date last week${place}?`, answer: "Search the same location with “last week” to calculate the previous calendar week." },
  ];
  if (data.period === "last_week") return [
    { question: `When did last week begin${place}?`, answer: `Last week${place} began on ${date}.` },
    { question: `What day was it last week${place}?`, answer: `The previous Monday-based calendar week${place} began on ${date}.` },
    { question: `What is the date next week${place}?`, answer: "Search the same location with “next week” to calculate the upcoming calendar week." },
  ];
  if (data.period === "tomorrow") return [
    { question: "What is tomorrow's date?", answer: `Tomorrow is ${date}.` },
    { question: "What day is tomorrow called?", answer: `Tomorrow is ${date}.` },
    { question: "Will tomorrow be a special day?", answer: "Holiday or observance status depends on country and region." },
  ];
  if (data.period === "yesterday") return [
    { question: "What was yesterday's date?", answer: `Yesterday was ${date}.` },
    { question: "What day was yesterday?", answer: `Yesterday was ${date}.` },
    { question: "What is tomorrow's date?", answer: "Tomorrow is one calendar day after today." },
  ];
  return [
    { question: "How do you write today's date?", answer: `A common full written form is ${date}.` },
    { question: "What is date mm dd yyyy?", answer: "MM/DD/YYYY means month, day, then four-digit year." },
    { question: "What was yesterday's date?", answer: "Search “what was yesterday's date?” for the deterministic result." },
  ];
}

export async function renderTodaysDate(container: HTMLElement, query: string): Promise<void> {
  container.innerHTML = `<div class="aye-weather-loading">Loading date…</div>`;

  let data: TimeApiResponse | null = null;
  try {
    data = await fetchDeterministicDate(normalizeQuery(query));
  } catch (error) {
    console.error("AyeSearch deterministic date lookup failed:", error);
  }

  if (!data) {
    const value = normalizeQuery(query).toLowerCase();
    const localOnly =
      !/\b(in|for)\s+[a-z]/i.test(value) &&
      !/\b(tomorrow|yesterday|next week|last week|ago|from today|from now|later)\b/.test(value);

    if (!localOnly) {
      container.innerHTML = `
        <div class="aye-date-page">
          <section class="aye-date-card" aria-label="Date result">
            <div class="aye-date-context">Date result is temporarily unavailable.</div>
          </section>
        </div>`;
      return;
    }

    let displayLocation = "";
    try {
      displayLocation = (await getFreshWeatherLocationContext())?.displayLabel || "";
    } catch {
      displayLocation = "";
    }
    data = {
      date: localLongDate(new Date()),
      period: "current_date",
      location: displayLocation,
      deterministic: true,
      response_type: "date",
    };
  }

  const dateText = String(data.date || "").trim();
  const location = String(data.location || "").trim();
  const related = relatedQuestions(data);

  container.innerHTML = `
    <div class="aye-date-page">
      <section class="aye-date-card" aria-label="Date result">
        <div class="aye-date-value">${escapeHtml(dateText)}</div>
        <div class="aye-date-context">
          ${location ? `Date in ${escapeHtml(location)}` : "AyeSearch date result"}
        </div>
      </section>

      <section class="aye-date-thinking" aria-labelledby="aye-date-thinking-title">
        <h2 id="aye-date-thinking-title">People also search for</h2>
        <div class="aye-date-questions">
          ${related.map((item, index) => `
            <div class="aye-date-question">
              <button type="button" class="aye-date-question-toggle"
                aria-expanded="false" aria-controls="aye-date-answer-${index}"
                data-date-question="${index}">
                <span>${escapeHtml(item.question)}</span>
                <span class="aye-date-chevron" aria-hidden="true">⌄</span>
              </button>
              <div class="aye-date-answer" id="aye-date-answer-${index}" hidden>
                ${escapeHtml(item.answer)}
              </div>
            </div>`).join("")}
        </div>
      </section>

      <section class="aye-date-web-result" aria-label="CalendarDate.com result">
        <a class="aye-date-result-title" href="https://www.calendardate.com/"
          target="_blank" rel="noopener noreferrer">CalendarDate.com</a>
        <div class="aye-date-result-url">https://www.calendardate.com</div>
        <p>View calendars, dates, holidays, moon phases and other calendar information.</p>
      </section>
    </div>`;

  bindQuestions(container);
}
